import {
    DestroyRef,
    Directive,
    HostListener,
    input,
    OnInit,
    output,
} from '@angular/core';
import { FormControl, NgControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import '../../../conversai/utils/string-extensions';
import { debounceTime, distinctUntilChanged, Observable } from 'rxjs';

@Directive({
    selector: '[aiCNPJ]',
    standalone: true,
})
export class CNPJDirective implements OnInit {
    public cnpjCompanyFinder =
        input.required<(cnpj: string) => Observable<any>>();
    public cnpjNameControl = input<FormControl>();
    public cnpjTraderNameControl = input<FormControl>();
    public cnpjCompanyFound = output<any>();
    public cnpjError = output<any>();

    constructor(
        private readonly destroyRef: DestroyRef,
        private readonly controlDir: NgControl,
    ) {}

    ngOnInit(): void {
        this.getCompany();
    }

    /**
     * Preenche os campos de nome e nome do trader com os dados
     * retornados pelo serviço de CNPJ.
     *
     * @param data Dados retornados pelo serviço de CNPJ.
     */
    private fillControls(data: any): void {
        if (!data) {
            this.cnpjError.emit({ notFound: true });
            return;
        }

        this.cnpjNameControl()?.setValue(data.name);
        this.cnpjTraderNameControl()?.setValue(data.traderName);
    }

    /**
     * Obtém do serviço os dados da empresa, caso o CNPJ informado
     * no campo tenha formação válida.
     */
    private getCompany(): void {
        const control = this.controlDir.control;

        if (!control) return;

        control.valueChanges
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                distinctUntilChanged(),
                debounceTime(1000),
            )
            .subscribe((value) => {
                const onlyNumbers = value?.replace(/[^a-zA-Z0-9]/g, '');

                if (onlyNumbers?.length === 14) {
                    this.cnpjCompanyFinder()(onlyNumbers)
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe({
                            next: (data: any) => {
                                this.fillControls(data);
                                this.cnpjCompanyFound.emit(data);
                            },
                            error: (error: any) => {
                                this.handleError(error.status);
                            },
                        });
                } else {
                    this.cnpjError.emit({ minLength: true });
                    this.cnpjNameControl()?.setValue('');
                    this.cnpjTraderNameControl()?.setValue('');
                }
            });
    }

    /**
     * Lida com erros ao buscar dados do CNPJ.
     * Atualiza o campo de nome do cliente e realiza as ações necessárias.
     *
     * @param errorStatus Objeto de erro retornado pelo serviço de CNPJ.
     */
    private handleError(errorStatus: number): void {
        this.cnpjNameControl()?.setValue('');
        this.cnpjTraderNameControl()?.setValue('');

        if (errorStatus === 404) {
            this.cnpjError.emit({ notFound: true });
        } else if (errorStatus === 400 || errorStatus === 422) {
            this.cnpjError.emit({ invalid: true });
        } else {
            this.cnpjError.emit({ serverError: true });
        }
    }

    @HostListener('input', ['$event'])
    onInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const originalValue = input.value;
        let value = input.value
            .replace(/[^a-zA-Z0-9]/g, '')
            .toUpperCase()
            .substring(0, 14);
        let digits = value.substring(12, 14).replace(/\D/g, '');
        value = value.substring(0, 12) + digits;
        const formattedValue = value.formatCNPJ();

        if (originalValue !== formattedValue) {
            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                window.HTMLInputElement.prototype,
                'value',
            )?.set;
            nativeInputValueSetter?.call(input, formattedValue);
            input.dispatchEvent(new Event('input', { bubbles: true }));
        }
    }
}
